import React from 'react';
import { type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { useFormElement } from '../useFormElement';

import { BaseRadioGroupInput, type BaseRadioGroupInputProps } from './BaseRadioGroupInput';

export type RadioGroupInputProps<T extends FieldValues, FP extends Path<T>> = Omit<
	BaseRadioGroupInputProps,
	'hasError' | 'name' | 'onChange' | 'ref' | 'value'
> & {
	defaultValue?: PathValue<T, FP>;
	name: FP;
	onChangeSelected?: (option?: string) => void; // added this cause onChange was omitted needed for data tracking
};

/**
 *
 * @param {RadioGroupInputProps} props - The component's props
 * @param {string} label - label to display
 * @param {string} children - use this in case label is not enough
 * @param {string} defaultChecked - initial checked state
 * @param {string} disabled - is the checkbox disabled
 * @param {boolean} isOnError - can be used to activate the on-error style
 * @param {function} onChange - on box click change callback
 */
export function RadioGroupInput<T extends FieldValues, FP extends Path<T> = Path<T>>({
	defaultValue,
	name,
	onChangeSelected,
	...props
}: RadioGroupInputProps<T, FP>) {
	const {
		field,
		fieldState: { error }
	} = useFormElement<T, FP>({
		defaultValue,
		name
	});

	return <BaseRadioGroupInput {...field} error={error} {...props} onChangeSelected={onChangeSelected} />;
}
